/**
 * Bildschirmfoto für „Problem melden" — mit geschwärzten Kundendaten (Baustein 17).
 *
 * 🔴 Gearbeitet wird an einer KOPIE des DOM. Die laufende Seite wird nie
 * verändert (kein Flackern, kein kaputter Zustand, wenn die Aufnahme abbricht).
 *
 * Geschwärzt wird:
 *  - jedes Element mit `data-pii` (Absender, Betreff, Mailtext, Anrufer,
 *    Zusammenfassung, Kontaktkarten, Termintitel, Chat …) — Inhalt raus,
 *    grauer Block in derselben Größe rein, Element und Layout bleiben;
 *  - JEDES Eingabefeld (input, textarea, select, contenteditable): dort steht
 *    fast immer etwas Kundenbezogenes, und ein vergessenes `data-pii` darf
 *    nicht zum Leck werden;
 *  - iframes (Mail-HTML) und Bilder.
 * Was `data-kein-foto` trägt (das Melde-Fenster selbst), fehlt ganz.
 *
 * 🔴 Seit 04.10.2026 GESCHLOSSEN statt offen: danach wird JEDER übrige Text
 * grau — außer in der Bedienung (`FREI_SELEKTOR`: Menü, Aktionsknöpfe,
 * Tabellenköpfe, Feldbeschriftungen, Reiter, `data-frei`). Vorher war alles
 * lesbar, was niemand mit `data-pii` markiert hatte (rund 20 Seiten ohne
 * Markierung: Kunden, Projekte, Aufgaben, WhatsApp …). Eine neue Seite ist
 * damit von Anfang an geschwärzt. Was trotzdem durchrutscht, schwärzt der
 * Nutzer in der Vorschau selbst (`bildMitBalken`).
 *
 * Kein html2canvas: die Kopie wird als SVG-`foreignObject` gezeichnet. Das
 * kommt ohne Fremdbibliothek aus; Schriften und Bilder von außen fehlen dabei
 * (gewollt — die Struktur reicht für ein Ticket).
 */

export const PII_SELEKTOR = "[data-pii]"
const FELD_SELEKTOR = "input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]), textarea, select, [contenteditable=''], [contenteditable=true]"
const MASKE_FARBE = "#9ca3af"
/** Bedienung, deren Text lesbar bleiben darf. Kundendaten gehören NIE hier
 *  hinein — im Zweifel lieber nicht `data-frei` setzen. */
export const FREI_SELEKTOR = "[data-frei], nav, .pp-btn, th, label, legend, [role=tab]"

// Attribute, die Inhalt tragen können — an geschwärzten Elementen weg.
const INHALT_ATTRIBUTE = ["title", "href", "value", "alt", "aria-label", "placeholder", "src", "srcdoc", "data-id"]

function grauerBlock(doc: Document, breite: number, hoehe: number, inline: boolean): HTMLElement {
  const b = doc.createElement("span")
  b.setAttribute("data-pii-maske", "")
  b.style.cssText = [
    `display:${inline ? "inline-block" : "block"}`,
    `width:${Math.max(Math.round(breite), 24)}px`,
    `height:${Math.max(Math.round(hoehe), 10)}px`,
    `max-width:100%`,
    `background:${MASKE_FARBE}`,
    `border-radius:3px`,
    `vertical-align:middle`,
  ].join(";")
  return b
}

function masse(el: Element | undefined): { b: number; h: number; inline: boolean } {
  if (!el) return { b: 80, h: 14, inline: true }
  const r = el.getBoundingClientRect()
  let inline = false
  try { inline = getComputedStyle(el).display.startsWith("inline") } catch { /* jsdom */ }
  return { b: r.width || 80, h: r.height || 14, inline }
}

function schwaerze(klon: Element, live: Element | undefined): void {
  const { b, h, inline } = masse(live)
  const doc = klon.ownerDocument
  for (const a of INHALT_ATTRIBUTE) klon.removeAttribute(a)
  const tag = klon.tagName.toLowerCase()
  if (tag === "input" || tag === "img" || tag === "iframe" || tag === "select" || tag === "textarea") {
    // Leere oder Ersatz-Elemente: ganz durch den Block ersetzen
    klon.replaceWith(grauerBlock(doc, b, h, true))
    return
  }
  while (klon.firstChild) klon.removeChild(klon.firstChild)
  klon.appendChild(grauerBlock(doc, b, h, inline))
}

/**
 * Kopie von `wurzel` mit geschwärzten Kundendaten. Die Live-Seite bleibt
 * unverändert. Reine DOM-Funktion — in Tests ohne Browser prüfbar.
 */
export function maskiereKlon(wurzel: HTMLElement): HTMLElement {
  const klon = wurzel.cloneNode(true) as HTMLElement

  // Das Melde-Fenster gehört nicht aufs Bild
  klon.querySelectorAll("[data-kein-foto], script, noscript").forEach((el) => el.remove())
  const liveOhne = Array.from(wurzel.querySelectorAll("[data-kein-foto]"))
  const ausserhalb = (el: Element) => !liveOhne.some((k) => k.contains(el))

  // Reihenfolge der Live- und Klon-Liste stimmt nur, wenn auf beiden Seiten
  // dasselbe fehlt — deshalb live die Elemente im Melde-Fenster ausfiltern.
  const paare = (sel: string) => {
    const live = Array.from(wurzel.querySelectorAll(sel)).filter(ausserhalb)
    const kopie = Array.from(klon.querySelectorAll(sel))
    return kopie.map((k, i) => [k, live[i]] as const)
  }

  // Beide Listen VOR dem ersten Eingriff bilden — danach stimmt die
  // Reihenfolge zwischen Kopie und Live-Seite nicht mehr.
  const felder = paare(`${FELD_SELEKTOR}, img, iframe, canvas, video`)
  const markiert = paare(PII_SELEKTOR)
  for (const [k, l] of felder) schwaerze(k, l)
  for (const [k, l] of markiert) schwaerze(k, l)
  textSchwaerzen(klon)
  return klon
}

/**
 * Jeder Text außerhalb der Bedienung → grauer Balken in ungefähr derselben
 * Breite. Der Wortlaut verlässt die Kopie (Buchstaben werden zu „x“), damit
 * auch ein CSS-Fehler beim Zeichnen nichts Lesbares hinterlässt.
 */
function textSchwaerzen(klon: HTMLElement): void {
  const doc = klon.ownerDocument
  const gang = doc.createTreeWalker(klon, NodeFilter.SHOW_TEXT)
  const knoten: Text[] = []
  for (let n = gang.nextNode(); n; n = gang.nextNode()) knoten.push(n as Text)
  for (const t of knoten) {
    const text = t.textContent || ""
    const el = t.parentElement
    if (!el || !text.trim()) continue
    if (el.closest("style, [data-pii-maske]") || el.closest(FREI_SELEKTOR)) continue
    if (el.closest("svg")) { t.textContent = ""; continue }   // Diagramm-Beschriftungen
    const balken = doc.createElement("span")
    balken.setAttribute("data-text-maske", "")
    balken.style.cssText = [
      `background:${MASKE_FARBE} !important`,
      "color:transparent !important",
      "-webkit-text-fill-color:transparent !important",
      "text-shadow:none !important",
      "border-radius:3px",
    ].join(";")
    balken.textContent = text.replace(/\S/g, "x")
    t.replaceWith(balken)
  }
  // Inhalt tragende Attribute außerhalb der Bedienung ebenfalls weg
  klon.querySelectorAll("*").forEach((el) => {
    if (el.closest(FREI_SELEKTOR)) return
    for (const a of INHALT_ATTRIBUTE) el.removeAttribute(a)
  })
}

/** Ein selbst gesetzter Balken, Anteile 0–1 der Bildbreite/-höhe. */
export interface Balken { x: number; y: number; b: number; h: number }

/** Zwei Punkte (Anteile) → Balken, egal in welche Richtung gezogen, auf das Bild begrenzt. */
export function balkenAusPunkten(a: { x: number; y: number }, c: { x: number; y: number }): Balken {
  const k = (v: number) => Math.min(1, Math.max(0, v))
  const x1 = k(Math.min(a.x, c.x)), x2 = k(Math.max(a.x, c.x))
  const y1 = k(Math.min(a.y, c.y)), y2 = k(Math.max(a.y, c.y))
  return { x: x1, y: y1, b: x2 - x1, h: y2 - y1 }
}

/**
 * Selbst gesetzte Balken FEST ins Bild malen (Pixel ersetzt, kein Überzug).
 * Ohne Balken kommt das Bild unverändert zurück. Wirft, wenn das Zeichnen
 * nicht geht — dann darf das Bild NICHT ohne Balken gesendet werden.
 */
export async function bildMitBalken(basis: string, balken: Balken[]): Promise<string> {
  if (!balken.length) return basis
  const bild = new Image()
  const geladen = new Promise<void>((ok, fehler) => {
    bild.onload = () => ok()
    bild.onerror = () => fehler(new Error("Bild konnte nicht geladen werden"))
  })
  bild.src = basis
  await geladen
  const leinwand = document.createElement("canvas")
  leinwand.width = bild.naturalWidth
  leinwand.height = bild.naturalHeight
  const ctx = leinwand.getContext("2d")
  if (!ctx) throw new Error("Kein Zeichenbereich verfügbar")
  ctx.drawImage(bild, 0, 0)
  ctx.fillStyle = "#000000"
  for (const z of balken) {
    ctx.fillRect(
      Math.floor(z.x * leinwand.width), Math.floor(z.y * leinwand.height),
      Math.ceil(z.b * leinwand.width) + 1, Math.ceil(z.h * leinwand.height) + 1,
    )
  }
  return leinwand.toDataURL("image/jpeg", 0.82)
}

function cssDerSeite(): string {
  let css = ""
  for (const blatt of Array.from(document.styleSheets)) {
    try {
      for (const regel of Array.from(blatt.cssRules)) css += regel.cssText + "\n"
    } catch { /* fremde Stylesheets (CORS) — ohne sie */ }
  }
  // :root/html/body gibt es im foreignObject nicht — auf die Hülle umbiegen
  return css
    .replace(/:root\b/g, ".pp-foto-root")
    .replace(/(^|[\s,{}])html(?=[\s,.:[{>~+])/g, "$1.pp-foto-root")
    .replace(/(^|[\s,{}])body(?=[\s,.:[{>~+])/g, "$1.pp-foto-body")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;")
}

/**
 * Bildschirmfoto der sichtbaren Seite als data:-URL (JPEG), geschwärzt.
 * Wirft, wenn der Browser das Zeichnen verweigert — der Aufrufer meldet das.
 */
export async function bildschirmfoto(wurzel: HTMLElement = document.body): Promise<string> {
  const breite = window.innerWidth
  const hoehe = window.innerHeight
  const klon = maskiereKlon(wurzel)
  const ser = new XMLSerializer()
  const inhalt = Array.from(klon.childNodes).map((n) => ser.serializeToString(n)).join("")
  const html = document.documentElement
  const huelle =
    `<div xmlns="http://www.w3.org/1999/xhtml" class="pp-foto-root ${escapeAttr(html.className)}" ` +
    `style="${escapeAttr(html.getAttribute("style") || "")};width:${breite}px;height:${hoehe}px;overflow:hidden;position:relative;">` +
    `<style>${cssDerSeite()}</style>` +
    `<div class="pp-foto-body ${escapeAttr(document.body.className)}" ` +
    `style="margin:0;width:${breite}px;transform:translateY(-${Math.round(window.scrollY)}px);">${inhalt}</div></div>`
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${breite}" height="${hoehe}">` +
    `<foreignObject x="0" y="0" width="100%" height="100%">${huelle}</foreignObject></svg>`

  const bild = new Image()
  bild.decoding = "sync"
  const geladen = new Promise<void>((ok, fehler) => {
    bild.onload = () => ok()
    bild.onerror = () => fehler(new Error("Bildschirmfoto konnte nicht gezeichnet werden"))
  })
  bild.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg)
  await geladen

  const massstab = Math.min(1, 1600 / breite)
  const leinwand = document.createElement("canvas")
  leinwand.width = Math.round(breite * massstab)
  leinwand.height = Math.round(hoehe * massstab)
  const ctx = leinwand.getContext("2d")
  if (!ctx) throw new Error("Kein Zeichenbereich verfügbar")
  ctx.fillStyle = "#ffffff"
  ctx.fillRect(0, 0, leinwand.width, leinwand.height)
  ctx.drawImage(bild, 0, 0, leinwand.width, leinwand.height)
  return leinwand.toDataURL("image/jpeg", 0.82)
}
